import { createFileRoute, Link } from "@tanstack/react-router";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";
import garenaKeyArt from "@/assets/blog/palworld-online-garena-mobile-cover.webp.asset.json";

const URL = "https://palstars.com/guides/palworld-mobile-garena";
const TITLE = "Palworld Garena Mobile Release Date Guide";
const DESCRIPTION =
  "Palworld garena mobile release date status, palworld mobile beta south korea download steps, how to play palworld mobile beta, and mobile pal catch rate calculators.";

const FAQ: FaqItem[] = [
  {
    q: "What is the Palworld Garena mobile release date?",
    a: "Garena has not published a global launch date for Palworld Online. South Korea is the active rollout market, with pre-registration open on Google Play and the App Store and closed beta testing running ahead of a 2026 release window. Any specific day circulating in community posts is not an official Garena date.",
  },
  {
    q: "How do you get the Palworld mobile beta South Korea download?",
    a: "Use the Korean Google Play or App Store listing for Palworld Online and pre-register there, then wait for Garena's beta invite. Closed beta access has been handed out through Garena's own signup form, not through open downloads, so APK mirrors claiming to be the beta are not official builds.",
  },
  {
    q: "How do you play the Palworld mobile beta from outside Korea?",
    a: "There is no supported way in. The store listing is region-locked and the beta client is tied to Garena accounts approved for the test region. Waiting for the wider release is safer than side-loading a package, which risks both your account and your device.",
  },
  {
    q: "Is Palworld Online the same game as Palworld on PC?",
    a: "No. Palworld Online is Garena's officially licensed mobile MMORPG built on Pocketpair's Palworld IP. It keeps Pal catching, base building and survival crafting, but adds a shared online world, co-op settlements and PvP built for touch controls.",
  },
  {
    q: "Do mobile Pal catch rate calculators work for the Garena version?",
    a: "Our sphere odds tools are built from the original Palworld capture formula, so use them as a planning reference rather than an exact beta figure. Garena has not published its own catch rate tables, and beta balance can shift before launch.",
  },
];

export const Route = createFileRoute("/guides/palworld-mobile-garena")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Palworld Guides", "/guides"],
        ["Palworld Mobile by Garena", "/guides/palworld-mobile-garena"],
      ]),
      faqSchema(FAQ, URL),
    ],
  }),
  component: GarenaMobileGuide,
});

function GarenaMobileGuide() {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-8">
      <nav className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>{" "}
        /{" "}
        <Link to="/guides" className="hover:text-foreground">
          Guides
        </Link>{" "}
        / <span className="text-foreground">Palworld Mobile by Garena</span>
      </nav>

      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld · mobile</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">
          Palworld Mobile by Garena: Release Date, Beta and Korea Download
        </h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          There is no confirmed Palworld Garena mobile release date yet. Garena's version, Palworld Online, is an
          officially licensed mobile MMORPG with Korea-first pre-registration on Google Play and the App Store and a
          closed beta ahead of a 2026 window. If you came here from the main game, the{" "}
          <Link to="/guides" className="text-primary hover:underline">
            Palworld tier list guides
          </Link>{" "}
          still cover the Pal roles the mobile build inherits.
        </p>
      </header>

      <figure className="overflow-hidden rounded-xl border border-border/60 bg-background/40">
        <img
          src={garenaKeyArt.url}
          alt="Palworld Online key art from Garena showing Pals and a trainer for the mobile MMORPG"
          width={1280}
          height={720}
          loading="lazy"
          decoding="async"
          className="aspect-video w-full object-cover"
        />
        <figcaption className="px-4 py-2 text-xs text-muted-foreground">
          Palworld Online key art — © Pocketpair, published on mobile by Garena.
        </figcaption>
      </figure>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Palworld Garena mobile release date status</h2>
        <p className="aeo-answer">
          Garena announced Palworld Online in August 2026 as a licensed mobile MMORPG rather than a straight port. Korean
          pre-registration opened in early September 2026 across both mobile stores, and Garena has run closed testing in
          South Korea and Taiwan. What is missing is a dated global launch, so treat the 2026 window as the only
          commitment on record.
        </p>
        <p>
          That staging matters if you are planning around it. A Korea-first live service usually means regional servers,
          Korean-language balance notes first, and a gap before other markets get store pages. We update this page when
          Garena moves the date instead of guessing at one.
        </p>
      </section>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Palworld mobile beta South Korea download steps</h2>
        <ol className="list-decimal space-y-2 pl-5">
          <li>Open the Korean Google Play or App Store listing for Palworld Online and pre-register on that account.</li>
          <li>Keep notifications on — beta invites and launch alerts go out through the store listing and Garena.</li>
          <li>Sign in with the Garena account you plan to keep; beta progress is tied to the account, not the handset.</li>
          <li>Ignore APK mirrors. Nothing outside the official store pages is a sanctioned beta client.</li>
        </ol>
        <p>
          If you sit outside the test region, there is no clean way in. Region locks apply to the store page and to the
          account approvals, so the honest answer to "how to play Palworld mobile beta" from abroad is: wait.
        </p>
      </section>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">What the beta gameplay actually shows</h2>
        <p>
          Store footage and Garena's own materials show Pal catching and hatching, third-person touch combat with a Pal
          partner, glider traversal, shared base building, production loops, dungeons and PvP duels. The survival core is
          recognizable; the difference is how much of it happens beside other players.
        </p>
        <p>
          Beta capture odds have not been published, so plan with the original formula. Our{" "}
          <Link to="/pal-catch" className="text-primary hover:underline">
            mobile Pal catch rate calculators
          </Link>{" "}
          run in a phone browser, and{" "}
          <Link to="/capture-rates" className="text-primary hover:underline">
            the capture rate table
          </Link>{" "}
          lists base rates per Pal. For the creature roster itself, use{" "}
          <Link to="/pals" className="text-primary hover:underline">
            all Palworld pals
          </Link>
          .
        </p>
      </section>

      <section className="glass rounded-2xl p-6 space-y-3 text-sm text-muted-foreground">
        <h2 className="text-xl font-bold text-foreground">Garena's version versus Krafton's version</h2>
        <p>
          Two separate mobile Palworld projects exist, and mixing them up is the single most common mistake in search
          results. Garena publishes Palworld Online, the MMORPG covered on this page. Krafton is developing a different
          title, Palworld Mobile, through PUBG Studios. Read the{" "}
          <Link to="/guides/palworld-mobile-krafton" className="text-primary hover:underline">
            Palworld Mobile by Krafton guide
          </Link>{" "}
          for that one, and the{" "}
          <Link to="/guides/$slug" params={{ slug: "palworld-platforms" }} className="text-primary hover:underline">
            platform and crossplay guide
          </Link>{" "}
          for the PC and console side.
        </p>
      </section>

      <FaqSection items={FAQ} heading="Palworld Mobile by Garena FAQ" />

      <section className="text-xs text-muted-foreground space-y-1">
        <h2 className="text-sm font-semibold text-foreground">Sources</h2>
        <p>
          <a
            href="https://www.garena.sg/news/7UPUTA"
            rel="nofollow ugc noopener noreferrer"
            target="_blank"
            className="text-primary hover:underline"
          >
            Garena — Palworld arrives as an MMORPG on mobile
          </a>
        </p>
        <p>
          <a
            href="https://www.pocketpair.jp/"
            rel="nofollow ugc noopener noreferrer"
            target="_blank"
            className="text-primary hover:underline"
          >
            Pocketpair — official site
          </a>
        </p>
      </section>
    </div>
  );
}
